/**
 * Patient group libs
 */

import { useState, useEffect } from 'react';
import { AppKey } from './apps';
import { useRequestOnEvent } from './hooks/useRequestJSON';
import { PostJSON, BaseResponse, IDParams } from './request';
import { usePatientGroup } from '../contexts/patient-group';
import { PatientGroupData } from '../contexts/patient-group/types';
import Toast from '../components/organisms/toast';
import { useMetaData } from '../contexts/metadata';

export interface PatientGroupFilters {
  contactIds?: number[];
  tagIds?: number[];
  apps?: AppKey[];
}

export interface IPatientGroup {
  id: number;
  name: string;
  isDefault: boolean;
  description: string;
  userId: number;
  accountId: number;
  filters: PatientGroupFilters;
  createdAt: Date;
  deletedAt: Date;
  updatedAt: Date;
}

export type IPatientGroupParams = Omit<
  IPatientGroup,
  'userId' | 'accountId' | 'createdAt' | 'deletedAt' | 'updatedAt'
>;

interface EditGroupResponse extends BaseResponse {
  group: IPatientGroup;
}

interface RemoveGroupResponse extends BaseResponse {
  id: number;
}

interface EditGroupValue {
  pgData: PatientGroupData;
  getActiveGroup: () => IPatientGroup;
  pgLoading: boolean;
  createPatientGroup: (params: Omit<IPatientGroupParams, 'id'>) => void;
  editPatientGroup: (params: IPatientGroupParams) => void;
  removePatientGroup: (params: IDParams) => void;
  editSuccess: string;
  editError: any;
  removeSuccess: boolean;
  removeError: any;
  resetRequests: () => void;
}

/**
 * Hooks
 */

export interface TogglePatientGroupFormFn {
  (type: 'new' | 'edit', close?: boolean): void;
}

interface PGFormState {
  isOpen: boolean;
  isNew: boolean;
}

interface TogglePatientGroupFormValue {
  pgForm: PGFormState;
  toggleForm: TogglePatientGroupFormFn;
}

export const useTogglePatientGroupForm = (): TogglePatientGroupFormValue => {
  const [pgForm, setPgForm] = useState({
    isOpen: false,
    isNew: false
  });

  // Type represents the type of form
  // we want to open
  const toggleForm: TogglePatientGroupFormFn = (type, close) => {
    let isOpen: boolean;
    let isNew: boolean;
    if (pgForm.isOpen) {
      if (type === 'new') {
        isNew = true;
      } else {
        isNew = false;
      }
      // Keep it open if it's already open, otherwise
      // if "close" = true, the user is clicking the "Close"
      // button or deleted a group, so we should close
      isOpen = true;
      if (close) {
        isOpen = false;
      }
    } else {
      // Form is closed and we want it to
      // stay that way
      if (close) {
        isOpen = false;
      } else {
        isOpen = true;
      }
      isNew = type === 'new' ? true : false;
    }
    setPgForm({ isOpen, isNew });
  };

  return {
    pgForm,
    toggleForm
  };
};

interface ActiveGroupNameValue {
  name: string;
  isAllPatient: boolean;
}

/**
 * Used to get the active group name
 * as well as if the active group is the all patient view
 * (and thus not one of the user defined patient groups)
 */
export const useGetActiveGroupName = (): ActiveGroupNameValue => {
  const { getActiveGroup, isLoading } = usePatientGroup();
  const { terms } = useMetaData();
  const activeGroup = getActiveGroup();
  const isAllPatient = !isLoading && !activeGroup;

  if (isLoading) {
    return {
      name: 'Loading...',
      isAllPatient
    };
  }
  /**
   * If patient groups are done loading, and we don't have
   * an active group, that means we're looking at All Patients
   */
  if (isAllPatient) {
    return {
      name: `All ${terms.patient.pluralCapitalized}`,
      isAllPatient
    };
  }
  return {
    name: activeGroup.name,
    isAllPatient
  };
};

/**
 * Used to both create new patient groups and edit existing ones
 * This hook also brings in patient group data via usePatientGroup
 *
 */
export const useEditPatientGroup = (): EditGroupValue => {
  const {
    data: pgData,
    updateGroup,
    removeGroup,
    getActiveGroup,
    isLoading: pgLoading
  } = usePatientGroup();
  const {
    data: editResult,
    error: editError,
    run,
    resetRequest
  } = useRequestOnEvent<IPatientGroupParams, EditGroupResponse>();
  const {
    data: removeResult,
    error: removeError,
    run: runRemove,
    resetRequest: resetRemoveRequest
  } = useRequestOnEvent<IDParams, RemoveGroupResponse>();
  const editStatus = editResult ? editResult.status : null;
  const removeStatus = removeResult ? removeResult.status : null;

  const resetRequests = () => {
    resetRequest();
    resetRemoveRequest();
  };

  const createPatientGroup: EditGroupValue['createPatientGroup'] = (params) => {
    resetRequests();
    const req = () =>
      PostJSON<EditGroupResponse, Omit<IPatientGroupParams, 'id'>>({
        url: '/patient/create_group/',
        params
      });
    run(req);
  };

  const editPatientGroup: EditGroupValue['editPatientGroup'] = (params) => {
    resetRequests();
    const req = () =>
      PostJSON<EditGroupResponse, IPatientGroupParams>({
        url: '/patient/edit_group/',
        params
      });
    run(req);
  };

  const removePatientGroup: EditGroupValue['removePatientGroup'] = (params) => {
    resetRequests();
    const req = () =>
      PostJSON<BaseResponse, IDParams>({
        url: '/patient/remove_group/',
        params
      });
    runRemove(req);
  };

  useEffect(() => {
    if (removeResult && removeResult.status === 'OK') {
      removeGroup(removeResult.id);
      Toast({
        intent: 'primary',
        message: 'This view has been removed!'
      });
    }
  }, [removeStatus]);

  useEffect(() => {
    if (editResult && editResult.status === 'OK') {
      updateGroup(editResult.group);
      Toast({
        intent: 'primary',
        message: 'This view has been updated!'
      });
    }
  }, [editStatus]);

  return {
    pgData,
    getActiveGroup,
    pgLoading,
    createPatientGroup,
    editPatientGroup,
    removePatientGroup,
    editSuccess: editResult && editResult.status === 'OK' ? 'Success!' : null,
    editError,
    removeError,
    removeSuccess: removeResult && removeResult.status === 'OK',
    resetRequests: resetRequests
  };
};
